import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../config/api';
import { DOCUMENT, documentSolidButtonSx, documentTextButtonSx } from '../constants/documentTheme';

const EmailAliasManager = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [aliases, setAliases] = useState([]);
  const [address, setAddress] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/inbound-email/address');
      setAddress(res.data.address || '');
      setAliases(res.data.aliases || []);
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.loadError'));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const token = searchParams.get('verifyAlias');
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await api.post('/inbound-email/aliases/verify', { token });
        if (!cancelled) {
          setMessage(t('emailForward.aliasVerified'));
          setSearchParams({});
          load();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || t('emailForward.aliasVerifyError'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, load, t]);

  const handleAdd = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.post('/inbound-email/aliases', { email: newEmail });
      setMessage(t('emailForward.aliasVerifySent'));
      setNewEmail('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.aliasAddError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await api.delete(`/inbound-email/aliases/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.aliasDeleteError'));
    }
  };

  return (
    <Box data-testid="email-alias-manager" sx={{ mt: 3, p: 2.5, border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface }}>
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, color: DOCUMENT.ink, mb: 1 }}>
        {t('emailForward.aliasesTitle')}
      </Typography>
      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2, lineHeight: 1.5 }}>
        {t('emailForward.aliasesBody', { address })}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Stack spacing={1} sx={{ mb: 2 }}>
        {aliases.length === 0 && (
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('emailForward.noAliases')}</Typography>
        )}
        {aliases.map((alias) => (
          <Box key={alias.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, color: DOCUMENT.ink }}>{alias.email}</Typography>
            <Chip
              size="small"
              label={alias.verified_at ? t('emailForward.verified') : t('emailForward.pending')}
              color={alias.verified_at ? 'success' : 'warning'}
            />
            <Button sx={documentTextButtonSx} onClick={() => handleDelete(alias.id)}>
              {t('emailForward.removeAlias')}
            </Button>
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder={t('emailForward.aliasPlaceholder')}
          sx={{ minWidth: 240, flex: 1 }}
        />
        <Button
          sx={documentSolidButtonSx}
          disabled={saving || !newEmail.trim()}
          onClick={handleAdd}
        >
          {saving ? t('emailForward.saving') : t('emailForward.addAlias')}
        </Button>
      </Box>
    </Box>
  );
};

export default EmailAliasManager;
